import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Board, BoardOffer, BoardStep, Story, StoryQuest, World, WorldObject, WorldPlaceRef } from '@mezha/api'
import { useEnterMutation, useLeaveMutation, useWorldQuery } from '../../api/endpoints/world'
import { useBoardInteractMutation, useBoardQuery, useStoryQuery } from '../../api/endpoints/quests'
import { useHeroQuery } from '../../api/endpoints/hero'
import { useCommand } from '../../lib/useCommand'
import { useWide } from '../../lib/useSize'
import { countdown, useRefetchAt, useServerNow } from '../../lib/time'
import { useShellMode, useSystemMessage } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem, type EmblemName } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { PlankButton, Sheet, cx } from '../../ui'
import { Dialog } from './Dialog'
import { InjuryCard } from '../injury/InjuryCard'
import { WorldMap } from '../map/geometry'
import { activeStoryQuest } from '../questNavigation'
import { fitsScene, scenePoint, unclutter, useSceneFrame, useSceneObstacle } from './scenePoint'
import { SceneMenu } from './SceneMenu'
import './location.css'
import '../questNavigation.css'

/** A thing to press on the scene: a place of the cell or an object of the place. */
type Target =
  | { kind: 'place'; id: string; name: string; place: WorldPlaceRef; x: number; y: number }
  | { kind: 'object'; id: string; name: string; object: WorldObject; x: number; y: number }
  | { kind: 'leave'; id: string; name: string; x: number; y: number }

const OBJECT_EMBLEM: Record<string, EmblemName> = { character: 'loc.talk', object: 'loc.inspect', board: 'loc.board', arena: 'nav.arena', exit: 'loc.exit', shop: 'loc.shop', mentor: 'nav.skills' }

/** Places of the cell, at their points on the picture of the cell. */
function placeTargets(world: World): Target[] {
  const cell = world.region.cells.find(c => c.x === world.x && c.y === world.y)
  return (cell?.places ?? []).map(p => ({ kind: 'place', id: p.id, name: p.name, place: p, x: p.x, y: p.y }))
}

/** Marks may contain a long quest/action subtitle, so collision checks reserve their CSS maximum. */
const plankWidth = () => 340

/** Objects of the place, and a way back to the cell when the place has no exit of its own. */
function objectTargets(world: World): Target[] {
  const objects: Target[] = (world.place?.objects ?? []).map(o => ({ kind: 'object', id: o.id, name: o.name, object: o, x: o.x, y: o.y }))
  if (!world.place?.objects.some(o => o.kind === 'exit')) objects.push({ kind: 'leave', id: '__leave', name: 'На клетку', x: 50, y: 80 })
  return objects
}

/** The story quest whose current step is this object in this place. */
export function storyFor(story: Story | undefined, placeId: string | null, objectId: string): StoryQuest | null {
  return story?.quests.find(q => q.status === 'active' && q.current?.object_id === objectId && q.current.place_id === placeId) ?? null
}

/** The accepted board offer whose current step needs this object (directly or as an alternative). */
export function boardFor(board: Board | undefined, placeId: string | null, objectId: string): { offer: BoardOffer; step: BoardStep } | null {
  for (const offer of board?.taken ?? []) {
    if (offer.status !== 'active') continue
    const current = offer.steps.find(s => !s.done)
    if (!current) continue
    const candidates = current.objective === 'any_of' ? current.alternatives ?? [] : [current]
    const step = candidates.find(s => s.target === objectId && s.place_id === placeId && ['interact', 'deliver', 'choice'].includes(s.objective))
    if (step) return { offer, step }
  }
  return null
}

const BOARD_VERB: Record<string, string> = { interact: 'Выполнить', deliver: 'Отдать', choice: 'Указать' }

/** One thing the player can do with a mark; the mark runs it on press (v04). */
type Verb = { id: string; verb: string; note?: string; emblem: EmblemName; tag?: string; run: () => Promise<unknown> | void }

export function Location() {
  const wide = useWide()
  useShellMode(wide ? 'default' : 'window', 'scene')
  const world = useWorldQuery()
  const hero = useHeroQuery()
  const story = useStoryQuery()
  const board = useBoardQuery()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const now = useServerNow(500)
  const [open, setOpen] = useState<string | null>(null)
  const [pending, setPending] = useState<string | null>(null)
  const [talk, setTalk] = useState<{ quest: StoryQuest; object: WorldObject } | null>(null)
  const [options, setOptions] = useState<{ offer: BoardOffer; step: BoardStep } | null>(null)
  const [enter] = useEnterMutation()
  const [leave] = useLeaveMutation()
  const [boardInteract] = useBoardInteractMutation()
  const cmd = useCommand(() => void world.refetch())
  const w = world.data
  const activeQuest = activeStoryQuest(story.data)
  const questStep = activeQuest?.current ?? null
  const onQuestCell = Boolean(w && questStep && w.x === questStep.x && w.y === questStep.y)

  const targets = useMemo(() => (w ? (w.place ? objectTargets(w) : placeTargets(w)) : []), [w])
  const [layer, setLayer] = useState<HTMLDivElement | null>(null)
  const [injuryNode, setInjuryNode] = useState<HTMLDivElement | null>(null)
  const frame = useSceneFrame(layer, w ? (w.place ? w.place.scene : w.cell_scene) : '')
  const injuryObstacle = useSceneObstacle(injuryNode, layer)
  const spots = useMemo(() => (frame ? unclutter(
    targets.map(t => ({ id: t.id, ...scenePoint(frame, t.x, t.y), width: plankWidth() })),
    injuryObstacle ? [injuryObstacle] : [], frame.width,
  ) : null), [frame, injuryObstacle, targets])
  const layoutKey = `${frame?.width}:${frame?.height}:${w?.place_id}:${targets.map(t => t.id).join(',')}`
  const [crowdedLayout, setCrowdedLayout] = useState('')
  const list = crowdedLayout === layoutKey || Boolean(frame && spots && !fitsScene(targets.map(t => ({ id: t.id, ...spots[t.id], width: plankWidth() })), frame))
  // Localised/quest text can make a plank taller than the geometry's usual reserve.
  // Measure the real buttons too, and keep this viewport in list mode if they cannot fit.
  useLayoutEffect(() => {
    if (!layer || !frame || list || frame.width < 600) return
    const measure = () => {
      const s = layer.getBoundingClientRect()
      const buttons = [...layer.querySelectorAll('.mark > .plank')].map(el => el.getBoundingClientRect())
      if (buttons.some((b, i) => b.left < s.left + 20 || b.right > s.right - 20 || b.top < s.top + 12 || b.bottom > s.bottom - 12 ||
        buttons.slice(i + 1).some(other => b.left < other.right + 8 && b.right + 8 > other.left && b.top < other.bottom + 8 && b.bottom + 8 > other.top))) setCrowdedLayout(layoutKey)
    }
    const ro = new ResizeObserver(measure)
    layer.querySelectorAll('.mark > .plank').forEach(el => ro.observe(el))
    measure()
    return () => ro.disconnect()
  }, [layer, frame, spots, list, layoutKey])
  useEffect(() => setOpen(null), [w?.place_id, w?.x, w?.y])

  const travel = w?.travel && Date.parse(w.travel.arrives_at) > now - 60_000 ? w.travel : null
  useRefetchAt(w?.travel?.arrives_at, world.refetch)
  const blocked = Boolean(travel) || Boolean(w?.encounter) || Boolean(w?.in_combat)
  const destination = travel && w ? new WorldMap(w).at(travel.to_x, travel.to_y) : null
  useSystemMessage(travel ? { kind: 'transition', text: travel.lost ? 'Идём в туман наугад' : `Идём: ${travel.to_region_name ?? destination?.name ?? 'соседняя клетка'}`, detail: countdown(now, travel.arrives_at).ms > 0 ? countdown(now, travel.arrives_at).text : 'Прибываем…' } : null)
  const lostTo = w?.lost && !travel ? new WorldMap(w).at(w.lost.intended_x, w.lost.intended_y) : null
  useSystemMessage(w?.lost && !travel ? { kind: 'transition', text: 'Туман сбил с пути', detail: `шли к «${lostTo?.name ?? ''}», вышли к «${w.region.cells.find(c => c.x === w.x && c.y === w.y)?.name ?? ''}»` } : null)
  useSystemMessage(w?.encounter ? { kind: 'transition', text: 'Встреча на клетке', detail: w.encounter.name } : null)

  const verbsFor = (t: Target): Verb[] => {
    if (!w) return []
    const leaveCell = () => cmd.run(() => leave({ expected_version: w.version }).unwrap())
    if (t.kind === 'place') {
      const questPlace = onQuestCell && questStep?.place_id === t.place.id
      return [{ id: 'enter', verb: 'Войти', note: questPlace ? `цель задания «${activeQuest?.title}»` : undefined, tag: questPlace ? 'по сюжету' : undefined,
        emblem: 'loc.enter', run: () => cmd.run(() => enter({ expected_version: w.version, place_id: t.place.id }).unwrap()) }]
    }
    if (t.kind === 'leave') {
      const towardQuest = onQuestCell && Boolean(w.place_id) && w.place_id !== questStep?.place_id
      return [{ id: 'leave', verb: 'Выйти', note: towardQuest ? `к цели «${questStep?.place_name}»` : 'на клетку', tag: towardQuest ? 'по сюжету' : undefined, emblem: 'loc.exit', run: leaveCell }]
    }
    const o = t.object
    if (o.kind === 'exit') {
      const towardQuest = onQuestCell && Boolean(w.place_id) && w.place_id !== questStep?.place_id
      return [{ id: 'leave', verb: 'Выйти', note: towardQuest ? `к цели «${questStep?.place_name}»` : 'на клетку', tag: towardQuest ? 'по сюжету' : undefined, emblem: 'loc.exit', run: leaveCell }]
    }
    if (o.kind === 'arena') return [{ id: 'arena', verb: 'На арену', emblem: 'nav.arena', run: () => navigate('/play/arena') }]
    if (o.kind === 'board') return [{ id: 'board', verb: 'Поручения', note: 'доска', emblem: 'loc.board', run: () => navigate('/play/journal?tab=board') }]
    if (o.kind === 'mentor' && o.mentor_id) return [{ id: 'mentor', verb: 'Обучаться', emblem: 'nav.skills', run: () => navigate(`/play/mentor/${encodeURIComponent(o.mentor_id!)}`) }]
    if (o.kind === 'shop' && o.shop_id) {
      const id = o.shop_id
      return [{ id: 'shop', verb: 'Торговать', emblem: 'loc.shop', run: () => navigate(`/play/shop/${encodeURIComponent(id)}`) }]
    }
    const verbs: Verb[] = []
    const quest = storyFor(story.data, w.place_id, o.id)
    if (quest?.current) {
      const kind = quest.current.kind
      verbs.push({
        id: 'story', verb: kind === 'battle' ? 'К бою' : o.kind === 'character' ? 'Говорить' : 'Осмотреть', note: `по сюжету: «${quest.current.title}»`, tag: 'по сюжету',
        emblem: kind === 'battle' ? 'act.strike' : o.kind === 'character' ? 'loc.talk' : 'loc.inspect', run: () => setTalk({ quest, object: o }),
      })
    }
    const task = boardFor(board.data, w.place_id, o.id)
    if (task) {
      verbs.push({
        id: 'task', verb: BOARD_VERB[task.step.objective] ?? 'Выполнить', note: `поручение «${task.offer.title}»`, tag: 'поручение', emblem: 'loc.task',
        run: async () => {
          if (task.step.objective === 'choice') return setOptions(task)
          const r = await cmd.run(() => boardInteract({ offer_id: task.offer.id, object_id: o.id }).unwrap())
          if (r) dispatch(ui.actions.notice(`Поручение «${task.offer.title}»: шаг выполнен`))
        },
      })
    }
    return verbs
  }

  const run = async (t: Target, v: Verb) => {
    if (pending) return
    setOpen(null)
    setPending(t.id)
    try {
      await v.run()
    } finally {
      setPending(null)
    }
  }

  if (talk && w) {
    return <Dialog quest={talk.quest} object={talk.object} onClose={() => setTalk(null)} />
  }

  const placeName = w?.place?.name ?? w?.region.cells.find(c => c.x === w.x && c.y === w.y)?.name ?? ''
  const sub = w ? (w.place ? w.region.name : `${w.region.name} · клетка ${w.x}, ${w.y}`) : ''

  return (
    <div className={cx('stage-scene location', list && 'location--list')} ref={setLayer}>
      {world.isLoading && <div className="location__state"><Loading text="Смотрим вокруг…" /></div>}
      {world.isError && !w && <div className="location__state"><Failure error={world.error} retry={() => void world.refetch()} what="Место" /></div>}
      {w && (
        <>
          <div className="placename">
            <h1><b>{placeName}</b></h1>
            <img className="flourish" src="/assets/ui/divider.webp" alt="" aria-hidden="true" />
            <span>{sub}</span>
          </div>
          {activeQuest && questStep && !onQuestCell && (
            <PlankButton gold className="location__quest-route" onClick={() => navigate('/play/map')}>
              <Emblem name="map.travel" size={18} />
              <span><b>{questStep.place_name}</b><small>цель задания «{activeQuest.title}» · показать путь на карте</small></span>
            </PlankButton>
          )}
          {w.place?.scene_text && <p className="location__lore">{w.place.scene_text}</p>}
          {hero.data?.injury && <div className="location__injury" ref={setInjuryNode}><InjuryCard hero={hero.data} placeId={w.place_id} onChange={() => { void hero.refetch(); void world.refetch() }} /></div>}
          {w.encounter && (
            <PlankButton gold className="location__encounter" onClick={() => navigate('/play/map')}>
              <Emblem name="map.danger" size={18} />Встреча: {w.encounter.name} — к карте ▸
            </PlankButton>
          )}
          <ul className="marks" aria-label={w.place ? 'Что здесь есть' : 'Места на клетке'}>
            {targets.map(t => {
              const o = t.kind === 'object' ? t.object : null
              const verbs = verbsFor(t)
              const isStory = verbs.some(v => v.id === 'story' || v.tag === 'по сюжету')
              const tag = verbs.find(v => v.tag)?.tag
              const busy = pending === t.id
              const single = verbs.length === 1 ? verbs[0] : null
              const visibleTag = tag && !single?.note?.includes(tag) ? tag : null
              const text = busy ? 'подождите…' : verbs.length ? `${verbs.map(v => v.verb.toLowerCase()).join(' · ')}${single?.note ? ` · ${single.note}` : ''}${visibleTag ? ` · ${visibleTag}` : ''}` : 'сейчас нечего делать'
              const menuId = `mark-menu-${t.id}`
              return (
                <li key={t.id} className={cx('mark', open === t.id && 'mark--selected', blocked && 'mark--locked', isStory && 'mark--story mark--quest-route', !verbs.length && 'mark--idle')}
                  style={spots?.[t.id] ?? { left: `${t.x}%`, top: `${t.y}%` }}>
                  <button type="button" className={cx('plank', blocked && 'plank--locked')} disabled={blocked || Boolean(pending) || !verbs.length}
                    aria-label={`${t.name}: ${text}`} aria-busy={busy || undefined}
                    aria-expanded={single ? undefined : open === t.id} aria-controls={single ? undefined : menuId}
                    onClick={() => (single ? void run(t, single) : setOpen(open === t.id ? null : t.id))}>
                    <span className="plank__in">
                      <Emblem name={t.kind === 'place' ? 'loc.enter' : t.kind === 'leave' ? 'loc.exit' : OBJECT_EMBLEM[o?.kind ?? 'object'] ?? 'loc.inspect'} size={18} />
                      <span className="mark__text">{t.name}<small className="mark__verb">{text}</small></span>
                    </span>
                  </button>
                  <div className="mark__ring" aria-hidden="true" />
                  {open === t.id && !single && (
                    <SceneMenu id={menuId} label={t.name}>
                      {verbs.map(v => (
                        <button key={v.id} type="button" role="menuitem" className="markmenu__row" onClick={() => void run(t, v)}>
                          <Emblem name={v.emblem} size={20} /><span><b>{v.verb}</b>{v.note && <small>{v.note}</small>}</span><i aria-hidden="true">▸</i>
                        </button>
                      ))}
                    </SceneMenu>
                  )}
                </li>
              )
            })}
            {targets.length === 0 && (
              <li className="location__empty">
                {w.place ? 'Здесь пока никого' : 'На этой клетке нет мест — идите дальше по карте'}
                {!w.place && <PlankButton onClick={() => navigate('/play/map')}>Открыть карту</PlankButton>}
              </li>
            )}
          </ul>
          {cmd.text && <p className="location__error" role="alert">{cmd.text}</p>}
        </>
      )}
      <Sheet open={Boolean(options)} title={options?.offer.title ?? ''} emblem="loc.task" onClose={() => setOptions(null)}>
        {options && <BoardOptions task={options} onDone={() => setOptions(null)} />}
      </Sheet>
    </div>
  )
}

/** A board step that asks to point at the right option; a wrong one returns a hint. */
function BoardOptions({ task, onDone }: { task: { offer: BoardOffer; step: BoardStep }; onDone: () => void }) {
  const [interact] = useBoardInteractMutation()
  const cmd = useCommand()
  const choose = async (option: string) => {
    const r = await cmd.run(() => interact({ offer_id: task.offer.id, object_id: task.step.target ?? '', option }).unwrap())
    if (r) onDone()
  }
  const wrongHint = cmd.error?.code === 'wrong_choice' && typeof cmd.error.details.hint === 'string' ? cmd.error.details.hint : null
  return (
    <div className="board-options">
      <p className="note">{task.step.text}</p>
      <ul className="choices">
        {(task.step.options ?? []).map((opt, i) => (
          <li key={opt}>
            <button type="button" className="choice-row" onClick={() => void choose(opt)}>
              <span className="gem gem--gold" aria-hidden="true">{i + 1}</span><b>{opt}</b>
            </button>
          </li>
        ))}
      </ul>
      {cmd.text && <p className="location__error" role="alert">{wrongHint ? `Не то. ${wrongHint}` : cmd.text}</p>}
    </div>
  )
}
